import { h, Component } from 'preact';
import isEqual from 'lodash/isEqual';
import { ZoomType } from '../../typing/enums';
import Placeholder from '../Placeholder/Placeholder';
import Error from '../Error/Error';
import { withContext } from '../../common/context';
import { FlexCenterStretched, imageCentered } from '../../utils/emotion';
import helpers from '../../utils/imageHelpers';
import { getAdjustedDims, checkSameRatio } from '../../utils/assetHelpers';
import { isMobile } from 'mobile-device-detect';

import { LazyComponent } from '../LazyComponent/LazyComponent';
const Zoom = LazyComponent(() => import(/* webpackChunkName: "zoom" */ '../Zoom/Zoom'));

class ImageAsset extends Component {
  state = {
    error: false,
    url: undefined,
    zoomUrl: undefined
  };

  componentDidMount() {
    const { active } = this.props;
    if (active) {
      this.initImage();
    }
  }

  componentDidUpdate(prevProps) {
    const { width, height, breakpoint, roundHeightToBreakpoint, zoom, zoomState } = this.props;

    const currDims = getAdjustedDims(
      prevProps.width,
      prevProps.height,
      breakpoint,
      roundHeightToBreakpoint
    );
    const nextDims = getAdjustedDims(width || 0, height || 0, breakpoint, roundHeightToBreakpoint);

    const isSameAspectRatio = checkSameRatio(
      currDims.width,
      currDims.height,
      nextDims.width,
      nextDims.height
    );

    const isSamePublicId = isEqual(prevProps.publicId, this.props.publicId);
    const isSameTransformation = isEqual(prevProps.transformation, this.props.transformation);
    const isActive = this.props.active && prevProps.active === false;

    if ((nextDims.width > currDims.width && isSameAspectRatio) || isActive) {
      this.initImage();
    } else if (!isSamePublicId || !isSameAspectRatio || !isSameTransformation) {
      this.resetImage();
      this.initImage();
    } else if (zoom && zoomState && !prevProps.zoomState) {
      this.initImage();
    }
  }

  resetImage() {
    this.setState({ url: undefined });
  }

  initImage() {
    this.setState({ error: false });

    const { zoomState } = this.props;

    if (zoomState) {
      helpers.loadZoomImage(this.props).then(
        asset => this.setState({ zoomUrl: asset.url }),
        () => {
          this.setState(this.state.url ? { zoomUrl: this.state.url } : { error: true });
        }
      );
    } else {
      helpers.loadHighResImage(this.props).then(
        asset => this.setState({ url: asset.url }),
        () => this.setState({ error: true })
      );
    }
  }

  render(props) {
    const { config } = props.context;
    const zoomType = config.selectZoomPropsType();
    const { initialZoom } = config.selectZoomPopupProps();

    return (
      <FlexCenterStretched
        data-test="image-wrap"
        data-ready={!!this.state.url}
        className={props.className}
      >
        {!this.state.error && !this.state.url && (props.width > 150 || props.height > 150) && (
          <Placeholder
            publicId={props.publicId}
            width={props.width}
            height={props.height}
            transformation={props.transformation}
            mediaType={props.mediaType}
            fetchPriority={props.fetchPriority}
            version={props.version}
          />
        )}
        {this.state.url && !this.state.error ? (
          <FlexCenterStretched relative data-test="img-zoom-wrap">
            <img
              itemprop="image"
              draggable={false}
              src={this.state.url}
              onLoad={props.onLoad}
              className={imageCentered}
              alt={props.alt}
              width={props.width}
              height={props.height}
              fetchPriority={props.fetchPriority}
            />
            {props.zoom && (
              <Zoom
                key={`zoom-${props.publicId}`}
                zoomIn={props.zoomState}
                setZoom={props.setZoom}
                width={props.width}
                height={props.height}
                url={this.state.zoomUrl || this.state.url}
                publicId={props.publicId}
                index={props.index}
                trigger={config.selectZoomPropsTrigger()}
                alt={props.alt}
                type={isMobile && zoomType !== ZoomType.POPUP ? ZoomType.INLINE : zoomType}
                viewerPosition={config.selectZoomPropsViewerPosition()}
                viewerContainer={isMobile ? undefined : config.selectZoomPropsContainer()}
                level={config.selectZoomPropsLevel()}
                inPopup={props.inPopup}
                initialZoom={initialZoom && props.inPopup && ZoomType.POPUP === zoomType}
              />
            )}
          </FlexCenterStretched>
        ) : null}
        {this.state.error ? <Error width={props.width} /> : null}
      </FlexCenterStretched>
    );
  }
}

export default withContext(ImageAsset);
